import {
  PUBLISHED_BLOG_POSTS,
  getBlogPostPath,
  type BlogImage,
  type PublishedBlogPost,
} from "../blog/posts";
import { TOOL_CATALOG } from "../tools/catalog";
import { TOOL_LANDING_CONTENT } from "../tools/landingContent";
import { TOOL_META_BY_SLUG } from "../tools/metaRegistry";
import type { ToolCategory, ToolMeta } from "../tools/types";

export type JsonLdNode = Record<string, unknown>;

export type JsonLdDocument = {
  "@context": "https://schema.org";
  "@graph": JsonLdNode[];
};

export type SeoDescriptor = {
  title: string;
  description: string;
  canonicalPath: string;
  robots?: string;
  schema?: JsonLdDocument;
};

export type SeoRoute = {
  path: string;
  descriptor: SeoDescriptor;
};

export const SITE_NAME = "No Gatekeeping";
export const DEFAULT_SITE_URL = "https://nogatekeeping.com";
export const MAX_RECOMMENDED_TITLE_LENGTH = 60;
export const SITE_DESCRIPTION =
  "Fast, free browser utilities for images, text, SEO checks, design workflows, conversions, extraction, and everyday cleanup.";

export const PAGE_CONTENT = {
  home: {
    title: "No Gatekeeping - Free Image, Text, and SEO Tools",
    description: SITE_DESCRIPTION,
    canonicalPath: "/",
  },
  tools: {
    title: "Free Online Image, Text & Design Tools - No Gatekeeping",
    description:
      "Use free online tools for image compression, image-to-text OCR, case conversion, image color picking, color palettes, SEO checks, and more.",
    canonicalPath: "/tools",
  },
  imageTools: {
    title: "Free Online Image Tools: Extract, Compress & Convert",
    description:
      "Use free online image tools to extract website images, compress or convert files, run image-to-text OCR, pick colors, and generate palettes.",
    canonicalPath: "/image-tools",
  },
  blog: {
    title: "Blog - No Gatekeeping",
    description:
      "Read No Gatekeeping field notes about SEO preflights, image cleanup, text utilities, design handoffs, URL workflows, and faster browser-tool work.",
    canonicalPath: "/blog",
  },
  about: {
    title: "About No Gatekeeping - Product and Editorial Standards",
    description:
      "Learn how No Gatekeeping reviews tool claims, publishes first-party workflow notes, discloses data handling, and keeps every utility useful without signup gates.",
    canonicalPath: "/about",
  },
  privacy: {
    title: "Privacy and Data Handling - No Gatekeeping",
    description:
      "See which No Gatekeeping tools process files locally, use browser storage, call site endpoints, or rely on third-party network services.",
    canonicalPath: "/privacy",
  },
  notFound: {
    title: "Page Not Found - No Gatekeeping",
    description:
      "This No Gatekeeping route does not exist. Head back home or browse the full tool directory.",
    canonicalPath: "/",
    robots: "noindex,follow",
  },
} satisfies Record<string, Omit<SeoDescriptor, "schema">>;

const APPLICATION_CATEGORY_BY_TOOL_CATEGORY: Record<ToolCategory, string> = {
  Text: "UtilitiesApplication",
  Images: "MultimediaApplication",
  URLs: "DeveloperApplication",
  SEO: "DeveloperApplication",
  Design: "DesignApplication",
  Other: "UtilitiesApplication",
};

export function normalizeSiteUrl(value: string) {
  return value.trim().replace(/\/+$/, "");
}

export function normalizePathname(pathname: string) {
  if (!pathname || pathname === "/") return "/";
  return `/${pathname.replace(/^\/+/, "").replace(/\/+$/, "")}`;
}

export function absoluteUrl(pathname: string, siteUrl = DEFAULT_SITE_URL) {
  const normalized = normalizePathname(pathname);
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
  return normalized === "/"
    ? `${normalizedSiteUrl}/`
    : `${normalizedSiteUrl}${normalized}`;
}

function organizationNode(siteUrl: string): JsonLdNode {
  return {
    "@type": "Organization",
    "@id": `${siteUrl}/#organization`,
    name: SITE_NAME,
    url: `${siteUrl}/`,
    sameAs: ["https://github.com/semifrontier/ng-app-new"],
  };
}

function websiteNode(siteUrl: string): JsonLdNode {
  return {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: SITE_NAME,
    url: `${siteUrl}/`,
    description: SITE_DESCRIPTION,
    publisher: {
      "@id": `${siteUrl}/#organization`,
    },
  };
}

function webpageNode(
  descriptor: Pick<SeoDescriptor, "title" | "description" | "canonicalPath">,
  pageType: string,
  siteUrl: string,
  mainEntity?: { "@id": string },
): JsonLdNode {
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);

  return {
    "@type": pageType,
    "@id": `${url}#webpage`,
    url,
    name: descriptor.title,
    description: descriptor.description,
    isPartOf: {
      "@id": `${siteUrl}/#website`,
    },
    publisher: {
      "@id": `${siteUrl}/#organization`,
    },
    ...(mainEntity ? { mainEntity } : {}),
  };
}

function toolUrl(tool: Pick<ToolMeta, "slug">, siteUrl: string) {
  return absoluteUrl(`/tools/${tool.slug}`, siteUrl);
}

function toolApplicationId(tool: Pick<ToolMeta, "slug">, siteUrl: string) {
  return `${toolUrl(tool, siteUrl)}#web-application`;
}

function appendTitleSuffix(title: string, suffix: string) {
  const titleWithSuffix = `${title}${suffix}`;

  return titleWithSuffix.length <= MAX_RECOMMENDED_TITLE_LENGTH
    ? titleWithSuffix
    : title;
}

function toolApplicationNode(meta: ToolMeta, siteUrl: string): JsonLdNode {
  return {
    "@type": "SoftwareApplication",
    "@id": toolApplicationId(meta, siteUrl),
    name: meta.pageHeading ?? meta.title,
    description: meta.seoDescription ?? meta.description,
    url: toolUrl(meta, siteUrl),
    applicationCategory: APPLICATION_CATEGORY_BY_TOOL_CATEGORY[meta.category],
    operatingSystem: "Any modern web browser",
    browserRequirements: "Requires JavaScript and a modern web browser",
    isAccessibleForFree: true,
    ...(meta.featureList?.length ? { featureList: meta.featureList } : {}),
    offers: {
      "@type": "Offer",
      price: 0,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    publisher: {
      "@id": `${siteUrl}/#organization`,
    },
  };
}

function faqPageNode(meta: ToolMeta, siteUrl: string): JsonLdNode | null {
  const faqs = TOOL_LANDING_CONTENT[meta.slug]?.faqs;
  if (!faqs?.length) return null;

  const url = toolUrl(meta, siteUrl);

  return {
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    name: `${meta.title} FAQ`,
    url,
    isPartOf: {
      "@id": `${url}#webpage`,
    },
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

function toolListNode(id: string, name: string, siteUrl: string): JsonLdNode {
  return {
    "@type": "ItemList",
    "@id": id,
    name,
    numberOfItems: TOOL_CATALOG.length,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: TOOL_CATALOG.map((tool, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: tool.title,
      url: absoluteUrl(tool.route, siteUrl),
      item: {
        "@id": toolApplicationId({ slug: tool.id }, siteUrl),
        "@type": "SoftwareApplication",
        name: tool.title,
        description: tool.desc,
        url: absoluteUrl(tool.route, siteUrl),
        applicationCategory:
          APPLICATION_CATEGORY_BY_TOOL_CATEGORY[tool.category as ToolCategory],
      },
    })),
  };
}

function breadcrumbNode(
  id: string,
  crumbs: Array<{ name: string; path: string }>,
  siteUrl: string,
): JsonLdNode {
  return {
    "@type": "BreadcrumbList",
    "@id": id,
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path, siteUrl),
    })),
  };
}

function blogImageObjectId(pageUrl: string, index: number) {
  return `${pageUrl}#image-${index + 1}`;
}

function blogImageObjectNode(
  image: BlogImage,
  pageUrl: string,
  siteUrl: string,
  index: number,
): JsonLdNode {
  const contentUrl = absoluteUrl(image.src, siteUrl);

  return {
    "@type": "ImageObject",
    "@id": blogImageObjectId(pageUrl, index),
    url: contentUrl,
    contentUrl,
    name: image.alt,
    description: image.alt,
    ...(image.caption ? { caption: image.caption } : {}),
    ...(image.credit ? { creditText: image.credit } : {}),
  };
}

function blogPostImages(post: PublishedBlogPost) {
  return [
    post.article.heroImage,
    ...post.article.sections.flatMap((section) =>
      section.image ? [section.image] : [],
    ),
  ];
}

function schemaGraph(nodes: JsonLdNode[], siteUrl: string): JsonLdDocument {
  return {
    "@context": "https://schema.org",
    "@graph": [organizationNode(siteUrl), websiteNode(siteUrl), ...nodes],
  };
}

function homeSchema(
  descriptor: SeoDescriptor,
  siteUrl: string,
): JsonLdDocument {
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);
  const listId = `${url}#tool-list`;

  return schemaGraph(
    [
      webpageNode(descriptor, "WebPage", siteUrl, { "@id": listId }),
      toolListNode(listId, "No Gatekeeping tool catalog", siteUrl),
    ],
    siteUrl,
  );
}

function toolsSchema(
  descriptor: SeoDescriptor,
  siteUrl: string,
): JsonLdDocument {
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);
  const listId = `${url}#tool-list`;

  return schemaGraph(
    [
      webpageNode(descriptor, "CollectionPage", siteUrl, { "@id": listId }),
      toolListNode(listId, "All No Gatekeeping tools", siteUrl),
      breadcrumbNode(
        `${url}#breadcrumb`,
        [
          { name: "Home", path: "/" },
          { name: "All Tools", path: "/tools" },
        ],
        siteUrl,
      ),
    ],
    siteUrl,
  );
}

function imageToolsSchema(
  descriptor: SeoDescriptor,
  siteUrl: string,
): JsonLdDocument {
  const imageTools = TOOL_CATALOG.filter((tool) =>
    [
      "image-extractor",
      "image-compressor",
      "image-converter",
      "image-to-text",
      "color-picker",
      "color-palette-generator",
    ].includes(tool.id),
  );
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);
  const listId = `${url}#image-tool-list`;

  return schemaGraph(
    [
      webpageNode(descriptor, "CollectionPage", siteUrl, { "@id": listId }),
      {
        "@type": "ItemList",
        "@id": listId,
        name: "No Gatekeeping image tools",
        numberOfItems: imageTools.length,
        itemListElement: imageTools.map((tool, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: tool.title,
          url: absoluteUrl(tool.route, siteUrl),
        })),
      },
      breadcrumbNode(
        `${url}#breadcrumb`,
        [
          { name: "Home", path: "/" },
          { name: "Image Tools", path: "/image-tools" },
        ],
        siteUrl,
      ),
    ],
    siteUrl,
  );
}

function blogSchema(
  descriptor: SeoDescriptor,
  siteUrl: string,
): JsonLdDocument {
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);
  const blogId = `${url}#blog`;

  return schemaGraph(
    [
      webpageNode(descriptor, "CollectionPage", siteUrl, { "@id": blogId }),
      {
        "@type": "Blog",
        "@id": blogId,
        name: "No Gatekeeping Blog",
        description: descriptor.description,
        url,
        blogPost: PUBLISHED_BLOG_POSTS.map((post) => ({
          "@type": "BlogPosting",
          "@id": `${absoluteUrl(getBlogPostPath(post), siteUrl)}#blog-post`,
          headline: post.title,
          url: absoluteUrl(getBlogPostPath(post), siteUrl),
        })),
        publisher: {
          "@id": `${siteUrl}/#organization`,
        },
      },
      breadcrumbNode(
        `${url}#breadcrumb`,
        [
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
        ],
        siteUrl,
      ),
    ],
    siteUrl,
  );
}

function infoPageSchema(
  descriptor: SeoDescriptor,
  label: string,
  siteUrl: string,
): JsonLdDocument {
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);

  return schemaGraph(
    [
      webpageNode(descriptor, "WebPage", siteUrl),
      breadcrumbNode(
        `${url}#breadcrumb`,
        [
          { name: "Home", path: "/" },
          { name: label, path: descriptor.canonicalPath },
        ],
        siteUrl,
      ),
    ],
    siteUrl,
  );
}

function blogPostSchema(
  descriptor: SeoDescriptor,
  post: PublishedBlogPost,
  siteUrl: string,
): JsonLdDocument {
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);
  const postId = `${url}#blog-post`;
  const imageNodes = blogPostImages(post).map((image, index) =>
    blogImageObjectNode(image, url, siteUrl, index),
  );

  return schemaGraph(
    [
      webpageNode(descriptor, "WebPage", siteUrl, { "@id": postId }),
      {
        "@type": "BlogPosting",
        "@id": postId,
        headline: post.title,
        description: post.excerpt,
        image: imageNodes.map((_, index) => ({
          "@id": blogImageObjectId(url, index),
        })),
        datePublished: post.article.publishedDate,
        dateModified: post.article.modifiedDate,
        author: {
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          name: post.article.author,
        },
        publisher: {
          "@id": `${siteUrl}/#organization`,
        },
        mainEntityOfPage: {
          "@id": `${url}#webpage`,
        },
      },
      ...imageNodes,
      breadcrumbNode(
        `${url}#breadcrumb`,
        [
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: getBlogPostPath(post) },
        ],
        siteUrl,
      ),
    ],
    siteUrl,
  );
}

function toolSchema(
  descriptor: SeoDescriptor,
  meta: ToolMeta,
  siteUrl: string,
): JsonLdDocument {
  const url = absoluteUrl(descriptor.canonicalPath, siteUrl);
  const appId = toolApplicationId(meta, siteUrl);
  const faqNode = faqPageNode(meta, siteUrl);

  return schemaGraph(
    [
      {
        ...webpageNode(descriptor, "WebPage", siteUrl, { "@id": appId }),
        ...(faqNode
          ? {
              hasPart: {
                "@id": faqNode["@id"],
              },
            }
          : {}),
      },
      toolApplicationNode(meta, siteUrl),
      ...(faqNode ? [faqNode] : []),
      breadcrumbNode(
        `${url}#breadcrumb`,
        [
          { name: "Home", path: "/" },
          { name: "All Tools", path: "/tools" },
          { name: meta.title, path: `/tools/${meta.slug}` },
        ],
        siteUrl,
      ),
    ],
    siteUrl,
  );
}

function toolDescriptor(meta: ToolMeta, siteUrl: string): SeoDescriptor {
  const descriptor = {
    title:
      meta.seoTitle ??
      appendTitleSuffix(meta.title, " - Free Tool - No Gatekeeping"),
    description: meta.seoDescription ?? meta.description,
    canonicalPath: `/tools/${meta.slug}`,
  };

  return {
    ...descriptor,
    schema: toolSchema(descriptor, meta, siteUrl),
  };
}

function blogPostDescriptor(
  post: PublishedBlogPost,
  siteUrl: string,
): SeoDescriptor {
  const descriptor = {
    title: appendTitleSuffix(post.title, " - No Gatekeeping"),
    description: post.excerpt,
    canonicalPath: getBlogPostPath(post),
  };

  return {
    ...descriptor,
    schema: blogPostSchema(descriptor, post, siteUrl),
  };
}

export function getSeoDescriptor(
  pathname: string,
  siteUrl = DEFAULT_SITE_URL,
): SeoDescriptor {
  const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
  const pathnameWithoutTrailingSlash = normalizePathname(pathname);

  if (pathnameWithoutTrailingSlash === "/") {
    return {
      ...PAGE_CONTENT.home,
      schema: homeSchema(PAGE_CONTENT.home, normalizedSiteUrl),
    };
  }

  if (pathnameWithoutTrailingSlash === "/tools") {
    return {
      ...PAGE_CONTENT.tools,
      schema: toolsSchema(PAGE_CONTENT.tools, normalizedSiteUrl),
    };
  }

  if (pathnameWithoutTrailingSlash === "/image-tools") {
    return {
      ...PAGE_CONTENT.imageTools,
      schema: imageToolsSchema(PAGE_CONTENT.imageTools, normalizedSiteUrl),
    };
  }

  if (pathnameWithoutTrailingSlash === "/blog") {
    return {
      ...PAGE_CONTENT.blog,
      schema: blogSchema(PAGE_CONTENT.blog, normalizedSiteUrl),
    };
  }

  if (pathnameWithoutTrailingSlash === "/about") {
    return {
      ...PAGE_CONTENT.about,
      schema: infoPageSchema(PAGE_CONTENT.about, "About", normalizedSiteUrl),
    };
  }

  if (pathnameWithoutTrailingSlash === "/privacy") {
    return {
      ...PAGE_CONTENT.privacy,
      schema: infoPageSchema(PAGE_CONTENT.privacy, "Privacy", normalizedSiteUrl),
    };
  }

  const blogPostMatch = pathnameWithoutTrailingSlash.match(/^\/blog\/([^/]+)$/);
  if (blogPostMatch) {
    const post = PUBLISHED_BLOG_POSTS.find(
      (item) => item.slug === blogPostMatch[1],
    );
    if (post) return blogPostDescriptor(post, normalizedSiteUrl);
  }

  const toolMatch = pathnameWithoutTrailingSlash.match(/^\/tools\/([^/]+)$/);
  if (toolMatch) {
    const entry = TOOL_META_BY_SLUG[toolMatch[1]];
    if (entry) return toolDescriptor(entry, normalizedSiteUrl);
  }

  const legacyToolMatch = pathnameWithoutTrailingSlash.match(/^\/tool\/([^/]+)$/);
  if (legacyToolMatch) {
    const entry = TOOL_META_BY_SLUG[legacyToolMatch[1]];
    if (entry) return toolDescriptor(entry, normalizedSiteUrl);
  }

  return PAGE_CONTENT.notFound;
}

export function getSeoPrerenderRoutes(
  siteUrl = DEFAULT_SITE_URL,
): SeoRoute[] {
  return [
    "/",
    "/tools",
    "/image-tools",
    "/blog",
    "/about",
    "/privacy",
    ...PUBLISHED_BLOG_POSTS.map((post) => getBlogPostPath(post)),
    ...TOOL_CATALOG.map((tool) => tool.route),
  ].map((path) => ({
    path,
    descriptor: getSeoDescriptor(path, siteUrl),
  }));
}
